import { useRequest } from "ahooks";
import dayjs from "dayjs";
import qs from "query-string";

import { ForwardRefRenderFunction, forwardRef, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "react-use";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StarRateIcon from "@mui/icons-material/StarRate";
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";

import { ApiFlightRoutesRecommendItem, getFlightRoutesRecommend } from "@/api/routes";
import { AIRPORT_MAP } from "@/constants/airport";
import { CabinMap } from "@/constants/config";
import { PROGRAM_IMG } from "@/constants/programs";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";

// eslint-disable-next-line react/display-name
const RoutesRecommend: ForwardRefRenderFunction<
  { refresh: (data: { origin: string[] }) => Promise<void> },
  {}
> = (_, forwardedRef) => {
  const theme = useThemeData();
  const { isPc } = usePlatform();
  const [visible, setVisible] = useState(false);

  const navigate = useNavigate();

  const { data, loading, runAsync } = useRequest(getFlightRoutesRecommend, {
    manual: true,
    debounceWait: 500,
  });

  const [storageData, setStorageData] = useLocalStorage<any[]>("home-route-recommend");

  const store = useGlobalStoreContext();

  const goRoutesPage = useEvent((data: ApiFlightRoutesRecommendItem) => {
    const diff = 3;
    const startDate = dayjs(data.date).subtract(diff, "d").unix();
    const endDate = dayjs(data.date).add(diff, "d").unix();
    const route = data.route.split("-");
    const query = {
      from: `${route[0]}_4`,
      to: `${route[1]}_4`,
      startDate,
      endDate,
    };
    navigate(`/routes?${qs.stringify(query)}`);
  });

  useImperativeHandle(forwardedRef, () => {
    return {
      refresh: async (params) => {
        const res = await runAsync(params);
        console.log(res.data);
        setStorageData(res.data);
      },
    };
  });

  if (!storageData?.length) {
    if ((loading && !data?.data) || !data?.data || data?.data?.length === 0) {
      return null;
    }
  }

  const _data = storageData || data.data;

  const fromCity = _data[0]?.route?.split("-")[0];

  return (
    <>
      <div className="mt-[50px] mx-[10px] md:mx-0 !mb[-500px]">
        <Typography
          className="flex items-center !mb-[10px]"
          color="textPrimary"
          fontWeight={20000}
          component={"div"}
        >
          <div className="flex flex-wrap items-center text-[16px] md:text-[20px]">
            {/* <StarRateIcon color="primary" /> */}
            {/* <StarRateIcon color="primary" /> */}
            <StarRateIcon color="primary" sx={{ mr: isPc ? 1 : 0 }} />
            Explore{" "}
            <Typography
              onClick={() => {
                navigate("/routes");
              }}
              className="inline !mx-[8px] !text-[20px] cursor-pointer"
              color="primary"
            >
              {isPc ? "Panorama Route" : "Panorama"}
            </Typography>{" "}
            from {AIRPORT_MAP[fromCity]?.city}
            <Tooltip
              sx={{ size: 10 }}
              title="These suggestions are based on the cheapest fares to popular destinations in the next year that we've seen in the last 36 hours. The price and availability can change quickly."
            >
              <InfoOutlinedIcon
                sx={{ fontSize: 16, marginLeft: "4px" }}
                onClick={() => {
                  if (!isPc) {
                    setVisible(true);
                  }
                }}
              />
            </Tooltip>
          </div>
        </Typography>

        <Grid container rowSpacing={2} spacing={2}>
          {_data.map((routeItem, index) => {
            const route = routeItem.route.split("-");
            const origin = route[0];
            const to = route[1];

            return (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  style={{
                    borderColor: theme.palette.divider,
                    borderWidth: 1,
                    borderStyle: "solid",
                    background: "none",
                  }}
                  className="rounded-[6px] cursor-pointer flex h-[155px] md:h-[160px]"
                  onClick={() => goRoutesPage(routeItem)}
                >
                  <div
                    className="rounded-tl-[2px] rounded-bl-[2px] w-[160px] md:w-[180px] bg-no-repeat bg-center bg-cover"
                    style={{
                      backgroundImage: `url(${routeItem.city_img})`,
                    }}
                  />
                  <div className="p-[10px] md:ml-[10px] flex-1">
                    <Typography fontSize={14} color="textPrimary" className="md:h-[46px]">
                      {`${AIRPORT_MAP[origin].city}(${origin}) - ${AIRPORT_MAP[to].city}(${to})`}
                    </Typography>
                    <div className="flex items-center md:space-x-[15px] space-x-[5px]">
                      <Typography fontSize={14} color="textPrimary">
                        {routeItem.date}
                      </Typography>
                      {routeItem.nonstop && (
                        <Typography color="textPrimary" fontSize={14}>
                          {routeItem.nonstop ? "nonstop" : ""}
                        </Typography>
                      )}
                    </div>
                    <div className="flex items-center md:space-x-[15px] space-x-[5px]">
                      <img className="w-[50px]" src={PROGRAM_IMG[routeItem.program]} alt="" />
                      <Typography color="textPrimary" fontSize={14}>
                        {CabinMap[routeItem.target_cabin]}
                      </Typography>
                    </div>

                    <Typography fontSize={14} className="!md:mt-[20px] !mt-[4px]">
                      Last Seen:{" "}
                      {routeItem.last_seen
                        ? dayjs(routeItem.last_seen * 1000).fromNow()
                        : "few days ago"}
                    </Typography>

                    <Typography
                      fontSize={14}
                      fontWeight={600}
                      color="primary"
                      className="font-bold text-right !md:mt-[20px] !mt-[4px]"
                    >
                      from {routeItem.price.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 })} pts{" "}
                      {routeItem.tax ? "+$" + routeItem.tax.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : ""}
                    </Typography>
                  </div>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </div>

      <Dialog
        open={visible}
        onClose={() => setVisible(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogContent>
          <Typography fontSize={16}>
            These suggestions are based on the cheapest fares to popular destinations in the next
            twelve month. Prices are for one way flight for one adult.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisible(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default forwardRef(RoutesRecommend);
